import type { ConvertableUnit } from "@/libs/units";
import { ToString, ToUnit } from "@/libs/units";

// Units carry converter functions, which can't be posted to or from a web
// worker. They cross as their registered label and are looked up on the
// other side, so both sides end up with the very same unit objects.
type EncodedUnit = { $unit: string };

const MAX_DEPTH = 50;

const isUnit = (value: object): value is ConvertableUnit =>
  typeof (value as ConvertableUnit).toThisUnit === "function" &&
  typeof (value as ConvertableUnit).toStandardUnit === "function";

const isEncodedUnit = (value: object): value is EncodedUnit =>
  Object.keys(value).length === 1 &&
  typeof (value as EncodedUnit).$unit === "string";

export const encodeUnit = (unit: ConvertableUnit): EncodedUnit => {
  const label = ToString(unit);
  if (ToUnit(label) !== unit) {
    throw new Error(`Unit "${label}" is not registered, so it can't be posted`);
  }
  return { $unit: label };
};

export const decodeUnit = ({ $unit }: EncodedUnit): ConvertableUnit => {
  const unit = ToUnit($unit);
  if (!unit) throw new Error(`Unknown unit "${$unit}"`);
  return unit;
};

// Deep copies value into plain data that can be posted: units are encoded,
// Vue proxies are read through, and anything else that can't be cloned
// throws rather than failing later inside postMessage.
export const encodeUnits = (value: unknown, depth = 0): unknown => {
  if (depth > MAX_DEPTH) throw new Error("Value is too deep to post");
  if (typeof value === "function") {
    throw new Error("Functions can't be posted to or from a worker");
  }
  if (value === null || typeof value !== "object") return value;
  if (isUnit(value)) return encodeUnit(value);
  if (Array.isArray(value)) return value.map(v => encodeUnits(v, depth + 1));
  return Object.fromEntries(
    Object.entries(value).map(([k, v]) => [k, encodeUnits(v, depth + 1)])
  );
};

export const decodeUnits = <T>(value: unknown, depth = 0): T => {
  if (depth > MAX_DEPTH) throw new Error("Value is too deep to decode");
  if (value === null || typeof value !== "object") return value as T;
  if (isEncodedUnit(value)) return decodeUnit(value) as T;
  if (Array.isArray(value)) {
    return value.map(v => decodeUnits(v, depth + 1)) as T;
  }
  return Object.fromEntries(
    Object.entries(value).map(([k, v]) => [k, decodeUnits(v, depth + 1)])
  ) as T;
};
